import React from "react";
import {
  getRiskClass,
  getRiskLabel,
  getPriorityClass,
  splitDriverSeverity,
} from "../../utils/riskUtils";

// The two halves of the "Evaluation Summary" comparison. The left column is
// either the original AI output or an earlier evaluation; the right column is
// always an evaluation snapshot taken from an evaluation_summary_updated audit.

// opts.driverTags splits each key driver's trailing severity ("- Critical")
// into a tag under the driver, as the Evaluation Summary dialog shows it.
export function renderAIBaselineColumn(rec, opts = {}) {
  if (!rec) return null;
  const riskClass = getRiskClass(rec.riskLevel);
  const hasAnyContent =
    rec.riskLevel ||
    rec.riskSummary ||
    (rec.keyDrivers && rec.keyDrivers.length > 0) ||
    (rec.recommendedActions && rec.recommendedActions.length > 0);
  if (!hasAnyContent) {
    return (
      <p className="eval-compare-empty">No AI summary available.</p>
    );
  }
  return (
    <>
      {rec.riskLevel ? (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">RISK LEVEL</div>
          <span className={`risk-badge ${riskClass}`}>
            {getRiskLabel(rec.riskLevel)}
          </span>
        </div>
      ) : null}
      {rec.riskSummary && (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">SUMMARY</div>
          <p className="eval-compare-text">{rec.riskSummary}</p>
        </div>
      )}
      {rec.keyDrivers && rec.keyDrivers.length > 0 && (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">KEY RISK DRIVERS</div>
          <ul className="eval-compare-list">
            {rec.keyDrivers.map((driver, idx) => {
              if (!opts.driverTags) {
                return (
                  <li key={idx}>
                    <span className="eval-compare-bullet"></span>
                    <span className="eval-compare-list-text">{driver}</span>
                  </li>
                );
              }
              const { text, severity } = splitDriverSeverity(driver);
              return (
                <li key={idx}>
                  <span className="eval-compare-bullet"></span>
                  <div className="eval-compare-list-text">
                    <div>{text}</div>
                    {severity && (
                      <div className="eval-compare-tag-row">
                        <span
                          className={`eval-compare-priority-pill ${getPriorityClass([severity])}`}
                        >
                          {severity}
                        </span>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {rec.recommendedActions && rec.recommendedActions.length > 0 && (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">RECOMMENDATIONS</div>
          <ul className="eval-compare-list">
            {rec.recommendedActions.map((action, idx) => {
              const tags = action.tags || [];
              const category = tags[0];
              const priorities = tags.slice(1);
              return (
                <li key={idx}>
                  <span className="eval-compare-bullet"></span>
                  <div className="eval-compare-list-text">
                    <div>{action.action}</div>
                    {tags.length > 0 && (
                      <div className="eval-compare-tag-row">
                        {category && (
                          <span className="eval-compare-category-pill">
                            → {category}
                          </span>
                        )}
                        {priorities.map((p, pi) => (
                          <span
                            key={pi}
                            className={`eval-compare-priority-pill ${getPriorityClass([p])}`}
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}

export function renderAuditEvalColumn(meta) {
  const m = meta || {};
  const riskLevel = m.updatedRiskLevel || 0;
  const riskClass = getRiskClass(riskLevel);
  const hasAnyContent =
    riskLevel ||
    m.evaluatorSummary ||
    m.keyRiskDriversHtml ||
    m.evaluatorRecommendations;
  if (!hasAnyContent) {
    return (
      <p className="eval-compare-empty">
        No snapshot recorded for this audit.
      </p>
    );
  }
  return (
    <>
      {riskLevel ? (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">RISK LEVEL</div>
          <span className={`risk-badge ${riskClass}`}>
            {getRiskLabel(riskLevel)}
          </span>
        </div>
      ) : null}
      {m.evaluatorSummary && (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">SUMMARY</div>
          <div
            className="eval-compare-html"
            dangerouslySetInnerHTML={{ __html: m.evaluatorSummary }}
          />
        </div>
      )}
      {m.keyRiskDriversHtml && (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">KEY RISK DRIVERS</div>
          <div
            className="eval-compare-html"
            dangerouslySetInnerHTML={{ __html: m.keyRiskDriversHtml }}
          />
        </div>
      )}
      {m.evaluatorRecommendations && (
        <div className="eval-compare-section">
          <div className="eval-compare-section-label">RECOMMENDATIONS</div>
          <div
            className="eval-compare-html"
            dangerouslySetInnerHTML={{ __html: m.evaluatorRecommendations }}
          />
        </div>
      )}
    </>
  );
}
